import {
    DocumentViewer,
    ProviderClaimTransmission,
    ProviderDocumentUrl,
    ProviderPayer,
    ProviderSubscriber,
    Spinner,
    TooltipTransitionComponent,
    X12ComparisonViewer,
} from '@enter/core';
import { mdiRomanNumeral1, mdiRomanNumeral2, mdiRomanNumeral3 } from '@mdi/js';
import { Tooltip } from '@mui/material';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { RootStore } from 'stores';
import * as S from './claim-timeline-table.styles';

interface ITransmissionDetailProps {
    transmission: ProviderClaimTransmission;
    sortedTransmissions?: ProviderClaimTransmission[]; // Pre-sorted by created date (newest first)
    showDiffWithPrevious?: boolean; // true for all transmissions except the oldest (shows diff with previous)
}

const LinePlaceholder = React.memo(() => {
    return (
        <ContentLoader speed={2} height={20} style={{ width: '100%' }} preserveAspectRatio="">
            <rect x="0" y="0" rx="0" ry="0" width="50%" height="20" />
        </ContentLoader>
    );
});

export const TransmissionStatus = ({ transmission }: { transmission: ProviderClaimTransmission }) => {
    if (!transmission.paymentIds || transmission.paymentIds.length === 0) {
        return (
            <Tooltip
                placement="top"
                title="Primary"
                TransitionComponent={TooltipTransitionComponent}
                enterTouchDelay={0}>
                <S.FlexWrapper>
                    <S.SecondaryIcon path={mdiRomanNumeral1} size={1} />
                </S.FlexWrapper>
            </Tooltip>
        );
    } else if (transmission.paymentIds.length === 1) {
        return (
            <Tooltip
                placement="top"
                title="Secondary"
                TransitionComponent={TooltipTransitionComponent}
                enterTouchDelay={0}>
                <S.FlexWrapper>
                    <S.SecondaryIcon path={mdiRomanNumeral2} size={1} />
                </S.FlexWrapper>
            </Tooltip>
        );
    } else if (transmission.paymentIds.length === 2) {
        return (
            <Tooltip
                placement="top"
                title="Tertiary"
                TransitionComponent={TooltipTransitionComponent}
                enterTouchDelay={0}>
                <S.FlexWrapper>
                    <S.SecondaryIcon path={mdiRomanNumeral3} size={1} />
                </S.FlexWrapper>
            </Tooltip>
        );
    }

    return null;
};

export const TransmissionDetail: React.FunctionComponent<ITransmissionDetailProps> = inject('rootStore')(
    observer(
        (props: ITransmissionDetailProps): React.ReactElement => {
            const rootStore: RootStore = _.get(props, 'rootStore');
            if (!rootStore) {
                return <></>;
            }
            const { claimStore } = rootStore;
            const { transmission, sortedTransmissions, showDiffWithPrevious } = props;

            const previousTransmission = useMemo(() => {
                if (!showDiffWithPrevious || !sortedTransmissions || sortedTransmissions.length < 2) {
                    return undefined;
                }
                
                // sortedTransmissions is already sorted by created date (newest first)
                const currentIndex = sortedTransmissions.findIndex(t => t.id === transmission.id);
                // Previous (older) transmission is the next one in the array when sorted newest first
                return currentIndex >= 0 && currentIndex < sortedTransmissions.length - 1 ? sortedTransmissions[currentIndex + 1] : undefined;
            }, [showDiffWithPrevious, sortedTransmissions, transmission.id]);

            const showDiff = !!previousTransmission?.x12 && !!transmission.x12;

            const [loading, setLoading] = useState(false);
            const [payer, setPayer] = useState<ProviderPayer | undefined>();
            const [subscriber, setSubscriber] = useState<ProviderSubscriber | undefined>();
            const [documentLoading, setDocumentLoading] = useState(false);
            const [documentUrl, setFile] = useState<ProviderDocumentUrl | undefined>();
            const id = transmission.documentId;

            useEffect(() => {
                (async () => {
                    if (!id) {
                        return;
                    }

                    setDocumentLoading(true);

                    try {
                        const res = await claimStore.loadDocumentDownloadUrl(id);
                        setFile(res);
                    } finally {
                        setDocumentLoading(false);
                    }
                })();
            }, [id]);

            useEffect(() => {
                (async (): Promise<void> => {
                    if (transmission && transmission.payerSubscriberId) {
                        setLoading(true);
                        const payerSubscriber = await claimStore.findPayerSubscriber(transmission.payerSubscriberId);
                        if (payerSubscriber) {
                            setPayer(payerSubscriber.payer);
                            setSubscriber(payerSubscriber.subscriber);
                        }
                        setLoading(false);
                    }
                })();
            }, [transmission, claimStore]);

            const renderMessages = (): React.ReactElement => {
                if (!!transmission.statuses?.length) {
                    return (
                        <S.ExpandTBody>
                            <S.ExpandTR>
                                <S.ExpandTD>
                                    <S.ExpandTitle>Payer Responses</S.ExpandTitle>
                                </S.ExpandTD>
                            </S.ExpandTR>

                            <S.ExpandTR>
                                <S.ExpandSubTH>Status</S.ExpandSubTH>
                                <S.ExpandSubTH>Code</S.ExpandSubTH>
                                <S.ExpandSubTH>Message</S.ExpandSubTH>
                            </S.ExpandTR>
                            {transmission.statuses.map((status, i) => (
                                <S.ExpandTR key={i}>
                                    <S.ExpandTD>{status.status}</S.ExpandTD>
                                    <S.ExpandTD>{status.messageCode}</S.ExpandTD>
                                    <S.ExpandTD>{status.messages}</S.ExpandTD>
                                </S.ExpandTR>
                            ))}
                        </S.ExpandTBody>
                    );
                }
                return <></>;
            };

            return (
                <S.Wrapper>
                    <S.ExpandTable key={transmission.id} expand={true}>
                        <S.ExpandTBody>
                            {loading && (
                                <S.ExpandTR>
                                    <S.ExpandTD colSpan={3}>
                                        <Spinner />
                                    </S.ExpandTD>
                                </S.ExpandTR>
                            )}
                            {!loading && (
                                <>
                                    <S.ExpandTR>
                                        <S.ExpandTH>Payer</S.ExpandTH>
                                        <S.ExpandTD colSpan={3}>{payer && payer.name}</S.ExpandTD>
                                    </S.ExpandTR>
                                    <S.ExpandTR>
                                        <S.ExpandTH>Subscriber</S.ExpandTH>
                                        <S.ExpandTD colSpan={3}>
                                            {subscriber &&
                                                `${_.compact([
                                                    subscriber.first,
                                                    subscriber.middle,
                                                    subscriber.last,
                                                ]).join(' ')}`}
                                        </S.ExpandTD>
                                    </S.ExpandTR>
                                    {transmission.type && (
                                        <S.ExpandTR>
                                            <S.ExpandTH>Type</S.ExpandTH>
                                            <S.ExpandTD colSpan={3}>{transmission.type}</S.ExpandTD>
                                        </S.ExpandTR>
                                    )}
                                    {transmission.externalId && (
                                        <S.ExpandTR>
                                            <S.ExpandTH>External ID</S.ExpandTH>
                                            <S.ExpandTD colSpan={3}>{transmission.externalId}</S.ExpandTD>
                                        </S.ExpandTR>
                                    )}
                                    {transmission.payerIcn && (
                                        <S.ExpandTR>
                                            <S.ExpandTH>Payer ICN</S.ExpandTH>
                                            <S.ExpandTD colSpan={3}>{transmission.payerIcn}</S.ExpandTD>
                                        </S.ExpandTR>
                                    )}
                                </>
                            )}
                        </S.ExpandTBody>
                        {renderMessages()}
                        {documentLoading && <LinePlaceholder />}
                    </S.ExpandTable>
                    {documentUrl?.url && <DocumentViewer url={documentUrl.url} />}
                    
                    <X12ComparisonViewer
                        currentX12={transmission.x12 || ''}
                        previousX12={previousTransmission?.x12}
                        previousTransmissionId={previousTransmission?.id}
                        showDiff={showDiff}
                    />
                </S.Wrapper>
            );
        },
    ),
);
